/**
 * StepExecutor
 * 
 * Executes individual steps in a flow, handling actions, conditions, and timeouts.
 */

import { IStepExecutor } from '../types';
import {
  Step,
  StepResult,
  StepAction,
  ExecutionContext,
  WaitCondition,
  TargetRef,
  ArrivalStyle,
  BubbleStyle,
  HighlightStyle,
} from '../types';
import { MoodType, GestureType, TrailMode, Position } from '../types';
import { useSquiggleStore } from '../../../stores/squiggleStore';
import { RiveController } from '../engines/RiveController';
import { TrailEngine } from '../engines/TrailEngine';
import { TargetManager } from '../engines/TargetManager';
import { ConditionEvaluator } from './ConditionEvaluator';

export class StepExecutor implements IStepExecutor {
  private riveController: RiveController;
  private trailEngine: TrailEngine;
  private targetManager: TargetManager;
  private conditionEvaluator: ConditionEvaluator;
  
  // Animation state
  private animationInProgress: boolean = false;

  constructor(
    riveController: RiveController,
    trailEngine: TrailEngine,
    targetManager: TargetManager,
    conditionEvaluator: ConditionEvaluator
  ) {
    this.riveController = riveController;
    this.trailEngine = trailEngine;
    this.targetManager = targetManager;
    this.conditionEvaluator = conditionEvaluator;
  }

  /**
   * Execute a step
   */
  async execute(step: Step, context: ExecutionContext): Promise<StepResult> {
    // Check abort signal
    if (context.abortSignal.aborted) {
      return { status: 'cancelled' };
    }

    // Evaluate conditions
    if (step.if && step.if.length > 0) {
      const conditionsMet = this.conditionEvaluator.evaluateAll(step.if);
      if (!conditionsMet) {
        return {
          status: 'skipped',
          nextStepId: step.elseStepId,
        };
      }
    }

    // Execute action with timeout
    const timeoutMs = step.timeoutMs ?? 30000;

    try {
      await Promise.race([
        this.executeAction(step.action, context),
        this.createTimeout(timeoutMs, context.abortSignal),
      ]);

      return {
        status: 'completed',
        nextStepId: step.next,
      };
    } catch (error) {
      if (error instanceof Error && error.message === 'timeout') {
        return {
          status: 'timeout',
          nextStepId: step.next,
        };
      }

      if (error instanceof Error && error.message === 'cancelled') {
        return { status: 'cancelled' };
      }

      return {
        status: 'error',
        error: error instanceof Error ? error : new Error(String(error)),
      };
    }
  }

  /**
   * Create a timeout promise
   */
  private createTimeout(ms: number, abortSignal: AbortSignal): Promise<void> {
    return new Promise((_, reject) => {
      const timeoutId = setTimeout(() => reject(new Error('timeout')), ms);

      abortSignal.addEventListener('abort', () => {
        clearTimeout(timeoutId);
        reject(new Error('cancelled'));
      });
    });
  }

  /**
   * Execute an action
   */
  private async executeAction(
    action: StepAction,
    context: ExecutionContext
  ): Promise<void> {
    switch (action.kind) {
      case 'moveTo':
        await this.executeMoveTo(action.target, action.arrival, context);
        break;

      case 'speak':
        await this.executeSpeak(action.text, action.bubble, context);
        break;

      case 'highlight':
        await this.executeHighlight(action.target, action.style);
        break;

      case 'gesture':
        await this.executeGesture(action.name);
        break;

      case 'setMood':
        this.executeSetMood(action.mood);
        break;

      case 'setTalking':
        this.executeSetTalking(action.value);
        break;

      case 'emitTrail':
        this.executeEmitTrail(action.mode);
        break;

      case 'waitFor':
        await this.executeWaitFor(action.condition, context);
        break;

      case 'dispatch':
        this.executeDispatch(action.event, action.payload);
        break;

      default:
        console.warn('[StepExecutor] Unknown action kind');
    }
  }

  /**
   * Execute moveTo action
   */
  private async executeMoveTo(
    target: TargetRef,
    arrival: ArrivalStyle | undefined,
    context: ExecutionContext
  ): Promise<void> {
    // Resolve target position
    const targetRect = await this.targetManager.waitForElement(target, 5000);
    if (!targetRect) {
      console.warn('[StepExecutor] Could not resolve target for moveTo');
      return;
    }

    const store = useSquiggleStore.getState();
    const currentPos = store.position;

    // Calculate target position
    const offsetPx = arrival?.offsetPx ?? 20;
    const targetPos: Position = {
      x: targetRect.x + targetRect.width / 2,
      y: targetRect.y - offsetPx,
    };

    // Start trail if configured
    if (arrival?.trail === 'draw') {
      this.trailEngine.setMode('on');
    }

    // Animate movement
    const duration = arrival?.durationMs ?? 500;
    const easing = arrival?.easing ?? 'ease';

    await this.animatePosition(currentPos, targetPos, duration, easing);

    // Stop trail
    if (arrival?.trail === 'draw') {
      this.trailEngine.setMode('fade');
    }
  }

  /**
   * Animate position from start to end
   */
  private async animatePosition(
    start: Position,
    end: Position,
    duration: number,
    easing: 'spring' | 'ease'
  ): Promise<void> {
    this.animationInProgress = true;
    const startTime = performance.now();

    return new Promise((resolve) => {
      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Apply easing
        const easedProgress = this.applyEasing(progress, easing);

        // Interpolate position
        const x = start.x + (end.x - start.x) * easedProgress;
        const y = start.y + (end.y - start.y) * easedProgress;

        // Update store
        useSquiggleStore.getState().setPosition({ x, y });

        // Add trail point
        if (this.trailEngine.getMode() === 'on') {
          this.trailEngine.addPoint({
            x,
            y,
            t: Date.now(),
          });
        }

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          this.animationInProgress = false;
          resolve();
        }
      };

      requestAnimationFrame(animate);
    });
  }

  /**
   * Apply easing function
   */
  private applyEasing(t: number, easing: 'spring' | 'ease'): number {
    switch (easing) {
      case 'spring':
        // Simple spring approximation
        return 1 - Math.pow(Math.cos(t * Math.PI / 2), 3);

      case 'ease':
      default:
        // Ease-in-out
        return t < 0.5
          ? 2 * t * t
          : 1 - Math.pow(-2 * t + 2, 2) / 2;
    }
  }

  /**
   * Execute speak action
   */
  private async executeSpeak(
    text: string | { [locale: string]: string },
    bubble: BubbleStyle | undefined,
    context: ExecutionContext
  ): Promise<void> {
    const store = useSquiggleStore.getState();

    // Resolve text (handle localization)
    const resolvedText = typeof text === 'string'
      ? text
      : text['en'] ?? Object.values(text)[0] ?? '';

    // Show bubble
    store.showBubble({
      text: resolvedText,
      placement: bubble?.placement ?? 'top',
      showNext: bubble?.showNext ?? false,
      showDismiss: bubble?.showDismiss ?? true,
      widthPx: bubble?.widthPx,
    });

    // Set talking state
    this.riveController.setTalking(true);
    store.setTalking(true);

    // Wait for user interaction or timeout
    if (bubble?.showNext) {
      await this.waitForBubbleNext(context);
    } else {
      // Auto-dismiss after reading time (estimate ~100ms per word)
      const wordCount = resolvedText.split(' ').length;
      const readingTime = Math.max(2000, wordCount * 100);
      await this.delay(readingTime);
    }

    // Stop talking
    this.riveController.setTalking(false);
    store.setTalking(false);
    store.hideBubble();
  }

  /**
   * Wait for bubble next button click
   */
  private waitForBubbleNext(context: ExecutionContext): Promise<void> {
    // Race between user clicking next and abort signal
    return Promise.race([
      context.waitForBubbleNext(),
      new Promise<void>((resolve) => {
        context.abortSignal.addEventListener('abort', () => resolve());
      }),
    ]);
  }

  /**
   * Execute highlight action
   */
  private async executeHighlight(
    target: TargetRef,
    style: HighlightStyle | undefined
  ): Promise<void> {
    // Resolve target
    const targetRect = await this.targetManager.waitForElement(target, 5000);
    if (!targetRect) {
      console.warn('[StepExecutor] Could not resolve target for highlight');
      return;
    }

    // For now, we just log - actual highlight would use spotlight system
    console.log('[StepExecutor] Highlight target:', targetRect, style);
  }

  /**
   * Execute gesture action
   */
  private async executeGesture(name: GestureType): Promise<void> {
    this.riveController.trigger(name);
    
    // Wait for gesture animation to complete (approximate duration)
    const gestureDurations: Record<GestureType, number> = {
      arrive: 800,
      celebrate: 1200,
      wave: 1000,
      point: 600,
      shrug: 800,
    };
    
    await this.delay(gestureDurations[name] ?? 500);
  }

  /**
   * Execute setMood action
   */
  private executeSetMood(mood: MoodType): void {
    this.riveController.setMood(mood);
    useSquiggleStore.getState().setMood(mood);
  }

  /**
   * Execute setTalking action
   */
  private executeSetTalking(value: boolean): void {
    this.riveController.setTalking(value);
    useSquiggleStore.getState().setTalking(value);
  }

  /**
   * Execute emitTrail action
   */
  private executeEmitTrail(mode: TrailMode): void {
    this.trailEngine.setMode(mode);
    useSquiggleStore.getState().setTrailMode(mode);
  }

  /**
   * Execute waitFor action
   */
  private async executeWaitFor(
    condition: WaitCondition,
    context: ExecutionContext
  ): Promise<void> {
    switch (condition.type) {
      case 'ms':
        await this.delay(condition.durationMs);
        break;

      case 'event':
        await this.waitForEvent(condition.name, context);
        break;

      case 'click':
        await this.waitForClick(condition.target, context);
        break;

      case 'inputNotEmpty':
        await this.waitForInputNotEmpty(condition.target, context);
        break;

      case 'route':
        await this.waitForRoute(condition.match, context);
        break;

      case 'elementPresent':
        await this.targetManager.waitForElement(condition.target);
        break;
    }
  }

  /**
   * Wait for an event
   */
  private waitForEvent(
    eventName: string,
    context: ExecutionContext
  ): Promise<void> {
    return new Promise((resolve) => {
      const handler = (event: CustomEvent) => {
        if (event.detail?.name === eventName) {
          window.removeEventListener('squiggle:event', handler as EventListener);
          resolve();
        }
      };

      window.addEventListener('squiggle:event', handler as EventListener);
      context.abortSignal.addEventListener('abort', () => {
        window.removeEventListener('squiggle:event', handler as EventListener);
        resolve();
      });
    });
  }

  /**
   * Wait for a click on target
   */
  private async waitForClick(
    target: TargetRef,
    context: ExecutionContext
  ): Promise<void> {
    // Wait for element to exist
    const rect = await this.targetManager.waitForElement(target);
    if (!rect) return;

    // Get element
    let element: Element | null = null;
    if (target.type === 'anchor') {
      const anchor = this.targetManager.getAnchor(target.id);
      element = anchor?.element ?? null;
    } else if (target.type === 'selector') {
      element = document.querySelector(target.selector);
    }

    if (!element) return Promise.resolve();

    // Store in const to help TypeScript narrowing
    const targetElement = element;

    return new Promise<void>((resolve) => {
      const handler = () => {
        targetElement.removeEventListener('click', handler);
        resolve();
      };

      targetElement.addEventListener('click', handler);
      context.abortSignal.addEventListener('abort', () => {
        targetElement.removeEventListener('click', handler);
        resolve();
      });
    });
  }

  /**
   * Wait for input to have value
   */
  private async waitForInputNotEmpty(
    target: TargetRef,
    context: ExecutionContext
  ): Promise<void> {
    const checkInterval = 100;

    return new Promise((resolve) => {
      const check = () => {
        if (context.abortSignal.aborted) {
          resolve();
          return;
        }

        let element: Element | null = null;
        if (target.type === 'anchor') {
          const anchor = this.targetManager.getAnchor(target.id);
          element = anchor?.element ?? null;
        } else if (target.type === 'selector') {
          element = document.querySelector(target.selector);
        }

        if (element && element instanceof HTMLInputElement) {
          if (element.value.trim().length > 0) {
            resolve();
            return;
          }
        }

        setTimeout(check, checkInterval);
      };

      check();
    });
  }

  /**
   * Wait for route change
   */
  private waitForRoute(
    match: import('../types').RouteMatch,
    context: ExecutionContext
  ): Promise<void> {
    return new Promise((resolve) => {
      const check = () => {
        if (this.conditionEvaluator.evaluate({ type: 'route', match })) {
          resolve();
        }
      };

      // Check immediately
      check();

      // Set up interval
      const intervalId = setInterval(check, 200);

      context.abortSignal.addEventListener('abort', () => {
        clearInterval(intervalId);
        resolve();
      });
    });
  }

  /**
   * Execute dispatch action
   */
  private executeDispatch(event: string, payload?: unknown): void {
    window.dispatchEvent(
      new CustomEvent('squiggle:event', {
        detail: { name: event, payload },
      })
    );
  }

  /**
   * Helper: delay for ms
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

export default StepExecutor;
